import type {
  ClientUserDto,
  PortalLoginResult,
  RegisterClientUserRequest,
  PortalLoginRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  PortalStepUpStatusDto,
  SpecimenOrderDto,
  SubmitOrderRequest,
  ListOrdersResult,
  UnreadCountDto,
  PortalResultDto,
  ListResultsResult,
  NotificationPreferencesDto,
  UpdatePreferencesRequest,
  NotificationsPageResult,
  SpecimenTypeDefinitionDto,
  ContainerTypeDto,
  LabLocationDto,
  ClinicalAiEligibilityDto,
  ClinicalAiReviewDto,
  ClinicalAiReviewRequest,
} from "@evolys/types";
import { ApiClient } from "./client";

export class PortalApi {
  constructor(private client: ApiClient) {}

  // Auth
  register(data: RegisterClientUserRequest): Promise<ClientUserDto> {
    return this.client.post<ClientUserDto>("/api/portal/auth/register", data);
  }

  verifyEmail(token: string): Promise<{ message: string }> {
    return this.client.post<{ message: string }>("/api/portal/auth/verify-email", { token });
  }

  login(data: PortalLoginRequest): Promise<PortalLoginResult> {
    return this.client.post<PortalLoginResult>("/api/portal/auth/login", data);
  }

  logout(): Promise<void> {
    return this.client.post<void>("/api/portal/auth/logout");
  }

  me(): Promise<PortalLoginResult> {
    return this.client.get<PortalLoginResult>("/api/portal/auth/me");
  }

  requestPasswordReset(data: RequestPasswordResetRequest): Promise<{ message: string }> {
    return this.client.post<{ message: string }>("/api/portal/auth/request-reset", data);
  }

  resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    return this.client.post<{ message: string }>("/api/portal/auth/reset-password", data);
  }

  getStepUpStatus(): Promise<PortalStepUpStatusDto> {
    return this.client.get<PortalStepUpStatusDto>("/api/portal/auth/step-up");
  }

  stepUp(password: string): Promise<PortalStepUpStatusDto> {
    return this.client.post<PortalStepUpStatusDto>("/api/portal/auth/step-up", {
      password,
    });
  }

  // Orders
  submitOrder(data: SubmitOrderRequest): Promise<SpecimenOrderDto> {
    return this.client.post<SpecimenOrderDto>("/api/portal/orders", data);
  }

  listMyOrders(params?: { page?: number; limit?: number; search?: string; status?: string }): Promise<ListOrdersResult> {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.search) query.set("search", params.search);
    if (params?.status) query.set("status", params.status);
    const qs = query.toString();
    return this.client.get<ListOrdersResult>(`/api/portal/orders${qs ? `?${qs}` : ""}`);
  }

  getOrder(id: string): Promise<SpecimenOrderDto> {
    return this.client.get<SpecimenOrderDto>(`/api/portal/orders/${id}`);
  }

  cancelOrder(id: string): Promise<{ message: string }> {
    return this.client.post<{ message: string }>(`/api/portal/orders/${id}/cancel`);
  }

  // Notifications
  listMyNotifications(params?: { page?: number; limit?: number }): Promise<NotificationsPageResult> {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.toString();
    return this.client.get<NotificationsPageResult>(`/api/portal/notifications${qs ? `?${qs}` : ""}`);
  }

  markNotificationRead(id: string): Promise<void> {
    return this.client.post<void>(`/api/portal/notifications/${id}/read`);
  }

  getUnreadCount(): Promise<UnreadCountDto> {
    return this.client.get<UnreadCountDto>("/api/portal/notifications/unread-count");
  }

  markAllNotificationsRead(): Promise<void> {
    return this.client.post<void>("/api/portal/notifications/mark-all-read");
  }

  // Results
  listMyResults(params?: { page?: number; limit?: number; search?: string }): Promise<ListResultsResult> {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    return this.client.get<ListResultsResult>(`/api/portal/results${qs ? `?${qs}` : ""}`);
  }

  getResult(orderId: string): Promise<PortalResultDto> {
    return this.client.get<PortalResultDto>(`/api/portal/results/${orderId}`);
  }

  downloadReportPdf(orderId: string, reportId: string): Promise<Blob> {
    return this.client.getBlob(`/api/portal/results/${orderId}/reports/${reportId}/pdf`);
  }

  getClinicalAiEligibility(
    orderId: string,
  ): Promise<ClinicalAiEligibilityDto> {
    return this.client.get<ClinicalAiEligibilityDto>(
      `/api/portal/results/${orderId}/clinical-ai`,
    );
  }

  reviewResultWithClinicalAi(
    orderId: string,
    request: ClinicalAiReviewRequest,
  ): Promise<ClinicalAiReviewDto> {
    return this.client.post<ClinicalAiReviewDto>(
      `/api/portal/results/${orderId}/clinical-ai`,
      request,
    );
  }

  viewSecureResultLink(
    linkId: string,
    token: string,
    accessCode: string,
    dateOfBirth: string,
  ): Promise<Blob> {
    return this.client.postBlob(
      `/api/portal/result-links/${linkId}/view`,
      { token, accessCode, dateOfBirth },
    );
  }

  // Preferences
  getPreferences(): Promise<NotificationPreferencesDto> {
    return this.client.get<NotificationPreferencesDto>("/api/portal/preferences");
  }

  updatePreferences(data: UpdatePreferencesRequest): Promise<NotificationPreferencesDto> {
    return this.client.put<NotificationPreferencesDto>("/api/portal/preferences", data);
  }

  // Reference Data
  listSpecimenTypeDefinitions(): Promise<SpecimenTypeDefinitionDto[]> {
    return this.client.get<SpecimenTypeDefinitionDto[]>(
      "/api/portal/reference-data/specimen-types",
    );
  }

  listContainerTypes(): Promise<ContainerTypeDto[]> {
    return this.client.get<ContainerTypeDto[]>(
      "/api/portal/reference-data/container-types",
    );
  }

  listCollectionLocations(): Promise<LabLocationDto[]> {
    return this.client.get<LabLocationDto[]>(
      "/api/portal/reference-data/collection-locations",
    );
  }
}
